import { useTranslation } from "react-i18next";

const UBIQUITI_GRAPH_RANGE_LABELS = ["1h", "1D", "1W", "1M"];

const UbiquitiGraphRangeSegmented = ({ ariaLabel }) => (
  <div className="ubiquiti-range-segmented" aria-label={ariaLabel}>
    {UBIQUITI_GRAPH_RANGE_LABELS.map((range) => (
      <button className={range === "1D" ? "is-active" : ""} type="button" disabled={range !== "1D"} key={range}>
        {range}
      </button>
    ))}
  </div>
);

const UbiquitiGraphViewTabs = ({ activeView, setActiveView }) => {
  const { t } = useTranslation();

  return (
    <div className="ubiquiti-segmented">
      <button className={activeView === "internet" ? "is-active" : ""} onClick={() => setActiveView("internet")}>Internet</button>
      <button className={activeView === "wifi" ? "is-active" : ""} onClick={() => setActiveView("wifi")}>WiFi</button>
      <button className={activeView === "flows" ? "is-active" : ""} type="button" onClick={() => setActiveView("flows")}>{t("Flows")}</button>
    </div>
  );
};

const UbiquitiWifiModeTabs = ({ activeWifiChartMode, setActiveWifiChartMode }) => {
  const { t } = useTranslation();

  return (
    <div className="ubiquiti-segmented ubiquiti-wifi-mode-tabs" aria-label={t("WiFi chart mode")}>
      <button
        className={activeWifiChartMode === "activity" ? "is-active" : ""}
        type="button"
        onClick={() => setActiveWifiChartMode("activity")}
      >
        Activity
      </button>
      <button
        className={activeWifiChartMode === "airtime" ? "is-active" : ""}
        type="button"
        onClick={() => setActiveWifiChartMode("airtime")}
      >
        Airtime
      </button>
    </div>
  );
};

const UbiquitiAirtimeSelectors = ({
  activeAirtimeAp,
  activeAirtimeRadio,
  airtimeApOptions,
  airtimeRadioOptions,
  isFetching,
  setActiveAirtimeAp,
  setActiveAirtimeRadio,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <label className="ubiquiti-source-select">
        <span className="ubiquiti-source-icon is-wifi" aria-hidden="true"></span>
        <select
          value={activeAirtimeAp}
          onChange={(event) => setActiveAirtimeAp(event.target.value)}
          disabled={isFetching}
          aria-label={t("Airtime AP selection")}
        >
          {airtimeApOptions.map((option) => (
            <option value={option.value} key={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      <label className="ubiquiti-source-select">
        <span className="ubiquiti-source-icon is-wifi" aria-hidden="true"></span>
        <select
          value={activeAirtimeRadio}
          onChange={(event) => setActiveAirtimeRadio(event.target.value)}
          disabled={isFetching}
          aria-label={t("Airtime radio selection")}
        >
          {airtimeRadioOptions.map((option) => (
            <option value={option.value} key={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
    </>
  );
};

const UbiquitiGraphSourceSelector = ({
  activeView,
  activeWifiFrequency,
  currentWan,
  setActiveWan,
  setActiveWifiFrequency,
  wanRows,
  wifiFrequencyOptions,
}) => {
  const { t } = useTranslation();

  return (
    <label className="ubiquiti-source-select">
      <span className={activeView === "internet" ? "ubiquiti-source-icon is-orange" : "ubiquiti-source-icon is-wifi"} aria-hidden="true">
      </span>
      <select
        value={activeView === "wifi" ? activeWifiFrequency : currentWan}
        onChange={(event) => {
          if (activeView === "wifi") {
            setActiveWifiFrequency(event.target.value);
            return;
          }

          setActiveWan(event.target.value);
        }}
        aria-label={activeView === "wifi" ? t("WiFi frequency selection") : t("WAN selection")}
      >
        {activeView === "internet" ? (
          <>
            <option value="all-wans">{t("All WANs")}</option>
            {wanRows.map((wan) => (
              <option value={wan.name} key={wan.name}>{wan.name}</option>
            ))}
          </>
        ) : (
          <>
            {wifiFrequencyOptions.map((option) => (
              <option value={option.value} key={option.value}>{option.label}</option>
            ))}
          </>
        )}
      </select>
    </label>
  );
};

export const UbiquitiInternetGraphControls = ({
  onToggleInternetActivity,
  toggleInternetSeries,
  usesIspInternetMetrics,
  visibleInternetSeries,
}) => {
  const { t } = useTranslation();

  if (!usesIspInternetMetrics) {
    return (
      <>
        <span><i className="is-traffic"></i>{t("Internet activity")}</span>
        <span><i className="is-latency"></i>{t("Average latency")}</span>
        <span><i className="is-loss"></i>{t("Packet loss")}</span>
      </>
    );
  }

  return (
    <>
      <button
        className={visibleInternetSeries.download || visibleInternetSeries.upload ? "is-active ubiquiti-activity-control" : "ubiquiti-activity-control"}
        type="button"
        onClick={onToggleInternetActivity}
        aria-pressed={visibleInternetSeries.download || visibleInternetSeries.upload}
      >
        <i className="is-activity"></i>
        {t("Internet Activity")}
        <span className="ubiquiti-control-chevron" aria-hidden="true"></span>
      </button>
      <button
        className={visibleInternetSeries.latency ? "is-active ubiquiti-check-control" : "ubiquiti-check-control"}
        type="button"
        onClick={() => toggleInternetSeries("latency")}
        aria-pressed={visibleInternetSeries.latency}
      >
        <span className="ubiquiti-toggle-check" aria-hidden="true"></span>
        <i className="is-isp-latency"></i>
        {t("Avg. Latency")}
      </button>
      <button
        className={visibleInternetSeries.packetLoss ? "is-active ubiquiti-check-control" : "ubiquiti-check-control"}
        type="button"
        onClick={() => toggleInternetSeries("packetLoss")}
        aria-pressed={visibleInternetSeries.packetLoss}
      >
        <span className="ubiquiti-toggle-check" aria-hidden="true"></span>
        <i className="is-loss"></i>
        {t("Packet loss")}
      </button>
    </>
  );
};

export const UbiquitiFlowsGraphControls = ({ flowsTotals, toggleFlowsSeries, visibleFlowsSeries }) => {
  const { t } = useTranslation();

  return (
    <>
      <button
        className={visibleFlowsSeries.allowed ? "is-active ubiquiti-check-control" : "ubiquiti-check-control"}
        type="button"
        onClick={() => toggleFlowsSeries("allowed")}
        aria-pressed={visibleFlowsSeries.allowed}
      >
        <span className="ubiquiti-toggle-check" aria-hidden="true"></span>
        <i className="is-download"></i>
        Allowed
        <span className="ubiquiti-legend-value">{flowsTotals.allowedDisplay}</span>
      </button>
      <button
        className={visibleFlowsSeries.blocked ? "is-active ubiquiti-check-control" : "ubiquiti-check-control"}
        type="button"
        onClick={() => toggleFlowsSeries("blocked")}
        aria-pressed={visibleFlowsSeries.blocked}
      >
        <span className="ubiquiti-toggle-check" aria-hidden="true"></span>
        <i className="is-loss"></i>
        Blocked
        <span className="ubiquiti-legend-value">{flowsTotals.blockedDisplay}</span>
      </button>
    </>
  );
};

export const UbiquitiAirtimeGraphControls = ({ toggleAirtimeSeries, visibleAirtimeSeries }) => {
  const { t } = useTranslation();

  return (
    <>
      <button
        className={visibleAirtimeSeries.totalAirtime ? "is-active ubiquiti-check-control" : "ubiquiti-check-control"}
        type="button"
        onClick={() => toggleAirtimeSeries("totalAirtime")}
        aria-pressed={visibleAirtimeSeries.totalAirtime}
      >
        <span className="ubiquiti-toggle-check" aria-hidden="true"></span>
        <i className="is-activity"></i>
        Total Airtime
      </button>
      <button
        className={visibleAirtimeSeries.multicast ? "is-active ubiquiti-check-control" : "ubiquiti-check-control"}
        type="button"
        onClick={() => toggleAirtimeSeries("multicast")}
        aria-pressed={visibleAirtimeSeries.multicast}
      >
        <span className="ubiquiti-toggle-check" aria-hidden="true"></span>
        <i className="is-warning"></i>
        Multicast
      </button>
    </>
  );
};

export const UbiquitiWifiActivityGraphControls = ({ toggleWifiSeries, visibleWifiSeries }) => {
  const { t } = useTranslation();

  return (
    <>
      <button
        className={visibleWifiSeries.activity ? "is-active ubiquiti-activity-control" : "ubiquiti-activity-control"}
        type="button"
        onClick={() => toggleWifiSeries("activity")}
        aria-pressed={visibleWifiSeries.activity}
      >
        <i className="is-activity"></i>
        {t("Internet Activity")}
        <span className="ubiquiti-control-chevron" aria-hidden="true"></span>
      </button>
      <button
        className={visibleWifiSeries.retries ? "is-active ubiquiti-check-control" : "ubiquiti-check-control"}
        type="button"
        onClick={() => toggleWifiSeries("retries")}
        aria-pressed={visibleWifiSeries.retries}
      >
        <span className="ubiquiti-toggle-check" aria-hidden="true"></span>
        <i className="is-warning"></i>
        {t("TX retries")}
      </button>
      <button
        className={visibleWifiSeries.clients ? "is-active ubiquiti-check-control" : "ubiquiti-check-control"}
        type="button"
        onClick={() => toggleWifiSeries("clients")}
        aria-pressed={visibleWifiSeries.clients}
      >
        <span className="ubiquiti-toggle-check" aria-hidden="true"></span>
        <i className="is-connections"></i>
        {t("WiFi clients")}
      </button>
    </>
  );
};

export default function UbiquitiGraphToolbar({
  activeAirtimeAp,
  activeAirtimeRadio,
  activeView,
  activeWifiChartMode,
  activeWifiFrequency,
  airtimeApOptions,
  airtimeRadioOptions,
  currentWan,
  flowsTotals,
  isAirtimeFetching,
  setActiveAirtimeAp,
  setActiveAirtimeRadio,
  setActiveView,
  setActiveWan,
  setActiveWifiChartMode,
  setActiveWifiFrequency,
  showToolbarSelector,
  toggleAirtimeSeries,
  toggleFlowsSeries,
  toggleInternetActivity,
  toggleInternetSeries,
  toggleWifiSeries,
  usesIspInternetMetrics,
  visibleAirtimeSeries,
  visibleFlowsSeries,
  visibleInternetSeries,
  visibleWifiSeries,
  wanRows,
  wifiFrequencyOptions,
}) {
  const { t } = useTranslation();
  const isWifiAirtimeView = activeView === "wifi" && activeWifiChartMode === "airtime";
  const hasInternetLegendGroup = usesIspInternetMetrics && activeView === "internet";

  return (
    <div className={`ubiquiti-toolbar ${!showToolbarSelector ? "without-selector" : ""}`}>
      <div className={`ubiquiti-toolbar-left ${activeView === "wifi" ? "has-wifi-mode" : ""}`}>
        <UbiquitiGraphViewTabs activeView={activeView} setActiveView={setActiveView} />
        {activeView === "wifi" ? (
          <UbiquitiWifiModeTabs activeWifiChartMode={activeWifiChartMode} setActiveWifiChartMode={setActiveWifiChartMode} />
        ) : null}
        {isWifiAirtimeView ? (
          <UbiquitiAirtimeSelectors
            activeAirtimeAp={activeAirtimeAp}
            activeAirtimeRadio={activeAirtimeRadio}
            airtimeApOptions={airtimeApOptions}
            airtimeRadioOptions={airtimeRadioOptions}
            isFetching={isAirtimeFetching}
            setActiveAirtimeAp={setActiveAirtimeAp}
            setActiveAirtimeRadio={setActiveAirtimeRadio}
          />
        ) : showToolbarSelector && (
          <UbiquitiGraphSourceSelector
            activeView={activeView}
            activeWifiFrequency={activeWifiFrequency}
            currentWan={currentWan}
            setActiveWan={setActiveWan}
            setActiveWifiFrequency={setActiveWifiFrequency}
            wanRows={wanRows}
            wifiFrequencyOptions={wifiFrequencyOptions}
          />
        )}
      </div>
      <div className="ubiquiti-toolbar-controls">
        <div className="ubiquiti-legend" role={hasInternetLegendGroup ? "group" : undefined} aria-label={hasInternetLegendGroup ? t("Internet chart series") : undefined}>
          {activeView === "internet" ? (
            <UbiquitiInternetGraphControls
              onToggleInternetActivity={toggleInternetActivity}
              toggleInternetSeries={toggleInternetSeries}
              usesIspInternetMetrics={usesIspInternetMetrics}
              visibleInternetSeries={visibleInternetSeries}
            />
          ) : activeView === "flows" ? (
            <UbiquitiFlowsGraphControls
              flowsTotals={flowsTotals}
              toggleFlowsSeries={toggleFlowsSeries}
              visibleFlowsSeries={visibleFlowsSeries}
            />
          ) : activeWifiChartMode === "airtime" ? (
            <UbiquitiAirtimeGraphControls
              toggleAirtimeSeries={toggleAirtimeSeries}
              visibleAirtimeSeries={visibleAirtimeSeries}
            />
          ) : (
            <UbiquitiWifiActivityGraphControls
              toggleWifiSeries={toggleWifiSeries}
              visibleWifiSeries={visibleWifiSeries}
            />
          )}
        </div>
      </div>
    </div>
  );
}
